import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { connect } from 'react-redux';

import * as Sentry from '@sentry/nextjs';

import * as APIClient from 'Services/APIClient';
import * as UserService from 'Services/UserService';

import { usePostPurchasePromoLayoutAbTest } from 'abTesting/postPurchasePromoLayout/hook';

import CouponShape from 'PropTypes/couponShape';

import LoadingScene from 'Scenes/LoadingScene';
import { CheckoutSuccess } from '../Scenes/CheckoutSuccess';

import useIsMountedRef from 'utils/useIsMounted';

import { clearCheckoutState as clearCheckoutStateAction } from 'dux/checkout/actions';
import { fetchOrderByPubkeyIfNeeded as fetchOrderByPubkeyIfNeededAction } from 'dux/checkoutOrder/actions';
import * as featureFlagsActions from 'dux/featureFlags/actions';
import {
  fetchUser as fetchUserAction,
  setCustomerOrigin as setCustomerOriginAction,
} from 'dux/user/actions';
import * as checkoutOrderSelectors from 'dux/checkoutOrder/selectors';
import { getOrderData, getOrderStatus } from 'dux/checkoutOrder/selectors';
import { getHasSelfies, getSelfiesSaved } from 'dux/selfies/selectors';
import { getUser } from 'dux/user/selectors';
import { useGetCartV2 } from 'dux/cartV2/hooks';
import * as flagVariants from 'dux/featureFlags/constants/flagVariants';
import { fetchGiftWithPurchaseList } from 'dux/giftWithPurchase/thunks';

import { CheckoutSuccessV2 } from './CheckoutSuccessV2';

const CheckoutSuccessContainer = ({
  currencyFromCheckoutOrder,
  orderHasSubscription,
  fetchOrderByPubkeyIfNeeded,
  hasSelfies,
  order,
  selfiesSaved,
  setCustomerOrigin,
  status,
  user,
  refreshGiftWithPurchaseList,
  subscriptionCoupons,
  offerCoupons,
  creditCoupons,
  deliveryBy,
  clearCheckoutState,
  fetchFeatureFlags,
  fetchUser,
}) => {
  const { variantName: postPurchasePromoLayoutVariant } = usePostPurchasePromoLayoutAbTest({
    isTrackingOnMount: true,
  });

  const { search } = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const pubkey = params.get('order-pubkey');
  // List of origins (e.g. Facebook, TikTok) that the user can select
  const [customerOrigins, setCustomerOrigins] = useState({
    isLoading: false,
    hasError: false,
    data: [],
  });
  // Origin selected by the user
  const [customerOriginState, setCustomerOriginState] = useState({ exists: false, success: false });
  const isMountedRef = useIsMountedRef();

  // refresh cartV2 after order success
  useGetCartV2();

  const [didRefetchFeatureFlags, setDidRefetchFeatureFlags] = useState(false);
  useEffect(() => {
    (async () => {
      // if the order is created, re-fetch the FFs (only once)
      if (order && !didRefetchFeatureFlags) {
        await fetchFeatureFlags();
        setDidRefetchFeatureFlags(true);
      }
    })();
  }, [order, didRefetchFeatureFlags]);

  const patchCustomerOrigin = useCallback(
    async customerOrigin => {
      try {
        const payload = {
          customer_origin: customerOrigin.value,
        };
        if (customerOrigin?.comment) {
          payload.customer_origin_comment = customerOrigin.comment;
        }
        await UserService.saveCustomerOrigin(payload);
        setCustomerOrigin(customerOrigin.value);
        if (isMountedRef.current) {
          setCustomerOriginState(prevCustomerOriginState => ({
            ...prevCustomerOriginState,
            success: true,
          }));
        }
      } catch (error) {
        Sentry.captureMessage('Error updating `customer_origin` from checkout success');
        throw error;
      }
    },
    [customerOriginState, setCustomerOrigins]
  );

  // Wee need this because we can click to another cart category
  useEffect(() => {
    clearCheckoutState();
    return () => {
      // Also clear on unmount to flush out the checkout order state
      clearCheckoutState();
    };
  }, [clearCheckoutState]);

  const [didRefetchUser, setDidRefetchUser] = useState(false);

  useEffect(() => {
    (async () => {
      if (order && !didRefetchUser) {
        fetchUser();
        setDidRefetchUser(true);
      }
    })();
  }, [order, fetchUser, didRefetchUser]);

  useEffect(() => {
    async function fetchCustomerOrigins() {
      if (isMountedRef.current) {
        setCustomerOrigins(prevCustomerOrigins => ({
          ...prevCustomerOrigins,
          isLoading: true,
        }));
      }

      try {
        const response = await APIClient.get(`/v1/customer_origins/`);
        const data = await response.json();

        // Randomize answers order, other should be last
        const other = data.find(d => d.name === 'Other');
        const origins = data
          .filter(d => d.name !== 'Other')
          .map(a => [Math.random(), a])
          .sort((a, b) => a[0] - b[0])
          .map(a => a[1]);

        if (isMountedRef.current) {
          setCustomerOrigins(prevCustomerOrigins => ({
            ...prevCustomerOrigins,
            data: [...origins, other],
            isLoading: false,
          }));
        }
      } catch (_err) {
        if (isMountedRef.current) {
          setCustomerOrigins(prevCustomerOrigins => ({
            ...prevCustomerOrigins,
            isLoading: false,
            hasError: true,
          }));
        }
      }
    }

    fetchCustomerOrigins();
    fetchOrderByPubkeyIfNeeded(pubkey);
    refreshGiftWithPurchaseList();
    if (user?.customer_origin !== null) {
      setCustomerOriginState(prevCustomerOriginState => ({
        ...prevCustomerOriginState,
        exists: true,
      }));
    }
  }, [user.customer_origin, pubkey]);

  if (!pubkey) {
    return <Navigate replace to="/account/history" />;
  }

  if (['idle', 'loading'].includes(status) || !didRefetchFeatureFlags) {
    return <LoadingScene />;
  }

  if (!order) {
    return <Navigate replace to="/account/history" />;
  }

  return postPurchasePromoLayoutVariant === flagVariants.VARIANT1 ? (
    <CheckoutSuccessV2
      customerOrigins={customerOrigins}
      customerOriginState={customerOriginState}
      onSubmitCustomerOrigin={patchCustomerOrigin}
      order={order}
      user={user}
    />
  ) : (
    <CheckoutSuccess
      creditCoupons={creditCoupons}
      currency={currencyFromCheckoutOrder}
      customerOrigins={customerOrigins}
      customerOriginState={customerOriginState}
      deliveryBy={deliveryBy}
      goToSelfieApp={() => navigate(`/selfie/top?pubkey=${order.pubkey}`)}
      hasSelfies={hasSelfies}
      offerCoupons={offerCoupons}
      onSubmitCustomerOrigin={patchCustomerOrigin}
      order={order}
      orderHasSubscription={orderHasSubscription}
      selfiesSaved={selfiesSaved}
      subscriptionCoupons={subscriptionCoupons}
      user={user}
    />
  );
};

CheckoutSuccessContainer.propTypes = {
  currencyFromCheckoutOrder: PropTypes.string.isRequired,
  deliveryBy: PropTypes.string.isRequired,
  orderHasSubscription: PropTypes.bool,
  fetchOrderByPubkeyIfNeeded: PropTypes.func.isRequired,
  refreshGiftWithPurchaseList: PropTypes.func.isRequired,
  hasSelfies: PropTypes.bool,
  order: PropTypes.shape({
    pubkey: PropTypes.string.isRequired,
  }),
  selfiesSaved: PropTypes.bool,
  setCustomerOrigin: PropTypes.func.isRequired,
  fetchUser: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  user: PropTypes.shape({
    customer_origin: PropTypes.string,
  }),
  subscriptionCoupons: PropTypes.arrayOf(CouponShape),
  offerCoupons: PropTypes.arrayOf(CouponShape),
  creditCoupons: PropTypes.arrayOf(CouponShape),
  clearCheckoutState: PropTypes.func.isRequired,
  fetchFeatureFlags: PropTypes.func.isRequired,
};

CheckoutSuccessContainer.defaultProps = {
  hasSelfies: false,
  order: null,
  selfiesSaved: false,
  user: null,
  subscriptionCoupons: null,
  offerCoupons: null,
  orderHasSubscription: false,
  creditCoupons: null,
};

export default connect(
  state => ({
    currencyFromCheckoutOrder: checkoutOrderSelectors.getCurrencyFromCheckoutOrder(state),
    deliveryBy: checkoutOrderSelectors.getDeliveryBy(state),
    orderHasSubscription: checkoutOrderSelectors.orderHasSubscription(state),
    hasSelfies: getHasSelfies(state),
    order: getOrderData(state),
    selfiesSaved: getSelfiesSaved(state),
    status: getOrderStatus(state),
    user: getUser(state),
    subscriptionCoupons: checkoutOrderSelectors.getOrderSubscriptionCoupons(state),
    offerCoupons: checkoutOrderSelectors.getOrderOfferCoupons(state),
    creditCoupons: checkoutOrderSelectors.getOrderCreditCoupons(state),
  }),
  {
    fetchOrderByPubkeyIfNeeded: fetchOrderByPubkeyIfNeededAction,
    setCustomerOrigin: setCustomerOriginAction,
    refreshGiftWithPurchaseList: fetchGiftWithPurchaseList,
    clearCheckoutState: clearCheckoutStateAction,
    fetchFeatureFlags: featureFlagsActions.fetchFeatureFlags,
    fetchUser: fetchUserAction,
  }
)(CheckoutSuccessContainer);
